import React, { useEffect, useState } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Route as RouteIcon, Clock, TrendingUp, ArrowRight, ExternalLink, Loader2 } from "lucide-react";

// Waypoints derived from the Google Maps links provided by the user.
// OSRM snaps these to actual roads.
const ROUTES = {
  "4passi": {
    color: "#FFE600",
    waypoints: [
      { name: "San Cassiano", coord: [46.5700687, 11.9360615] },
      { name: "Passo Campolongo", coord: [46.519605, 11.8737906] },
      { name: "Passo Pordoi", coord: [46.4888128, 11.8117568] },
      { name: "Passo Sella", coord: [46.509175, 11.7582768] },
      { name: "Passo Gardena", coord: [46.55, 11.809444] },
      { name: "Corvara", coord: [46.5475716, 11.8754005] },
    ],
    center: [46.5439, 11.8267],
    zoom: 11,
    mapsUrl: "https://maps.app.goo.gl/h49sBaz1XAPpCuDPA",
  },
  cortina: {
    color: "#FFE600",
    waypoints: [
      { name: "San Cassiano", coord: [46.5700687, 11.9360615] },
      { name: "Cortina d'Ampezzo", coord: [46.5378431, 12.1358821] },
    ],
    center: [46.5441, 11.9944],
    zoom: 11,
    mapsUrl: "https://maps.app.goo.gl/1KQULvTk2ew932zc6",
  },
  brunico: {
    color: "#FFE600",
    waypoints: [
      { name: "San Cassiano", coord: [46.5700687, 11.9360615] },
      { name: "Brunico", coord: [46.7965374, 11.9381326] },
    ],
    center: [46.6833, 11.9371],
    zoom: 11,
    mapsUrl: "https://maps.app.goo.gl/oEPXx9zYQxrGrkPj7",
  },
};

async function fetchOsrmRoute(waypoints) {
  // OSRM expects lon,lat;lon,lat...
  const coordsStr = waypoints.map(w => `${w.coord[1]},${w.coord[0]}`).join(";");
  const url = `https://router.project-osrm.org/route/v1/driving/${coordsStr}?overview=full&geometries=geojson&continue_straight=true`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("OSRM error");
  const json = await res.json();
  if (!json.routes || !json.routes[0]) throw new Error("No route");
  return json.routes[0];
}

function RouteMap({ routeId, latlons }) {
  const r = ROUTES[routeId];
  if (!r) return null;
  const positions = latlons && latlons.length > 0 ? latlons : r.waypoints.map(w => w.coord);
  return (
    <div className="relative h-72 sm:h-80 w-full overflow-hidden border-b border-white/10" data-testid={`route-map-${routeId}`}>
      <MapContainer
        center={r.center}
        zoom={r.zoom}
        scrollWheelZoom={false}
        zoomControl={false}
        attributionControl={false}
        style={{ height: "100%", width: "100%", background: "#0a0a0a" }}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          subdomains={['a', 'b', 'c', 'd']}
        />
        <Polyline positions={positions} pathOptions={{ color: r.color, weight: 4, opacity: 0.95 }} />
        {r.waypoints.map((w, i) => {
          const isEdge = i === 0 || i === r.waypoints.length - 1;
          return (
            <CircleMarker
              key={`${w.name}-${i}`}
              center={w.coord}
              radius={isEdge ? 7 : 4}
              pathOptions={{ color: r.color, fillColor: isEdge ? "#000" : r.color, fillOpacity: 1, weight: 2 }}
            >
              <Tooltip direction="top" offset={[0, -6]} permanent={isEdge} className="route-tooltip">{w.name}</Tooltip>
            </CircleMarker>
          );
        })}
      </MapContainer>
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/30 via-transparent to-transparent" />
    </div>
  );
}

function RouteCard({ item, idx, onBook }) {
  const { t } = useI18n();
  const r = ROUTES[item.id];
  const [latlons, setLatlons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = `osrm_${item.id}_v2`;
    const cached = (() => {
      try { return JSON.parse(localStorage.getItem(cacheKey) || "null"); } catch { return null; }
    })();
    if (cached && Array.isArray(cached) && cached.length > 1) {
      setLatlons(cached);
      setLoading(false);
      return;
    }
    fetchOsrmRoute(r.waypoints)
      .then(route => {
        // OSRM returns [lon, lat]; convert to [lat, lon]
        const ll = route.geometry.coordinates.map(([lon, lat]) => [lat, lon]);
        if (cancelled) return;
        setLatlons(ll);
        setLoading(false);
        try { localStorage.setItem(cacheKey, JSON.stringify(ll)); } catch (e) { console.warn("route cache write skipped (quota):", e?.message); }
      })
      .catch(() => {
        if (cancelled) return;
        // Fallback to straight waypoint lines
        setLatlons(r.waypoints.map(w => w.coord));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [item.id, r.waypoints]);

  const handleGpx = () => {
    if (r.mapsUrl) window.open(r.mapsUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <article
      className="group bg-[#0a0a0a] border border-white/10 hover:border-scooter-yellow/60 transition-colors flex flex-col overflow-hidden"
      data-testid={`route-card-${item.id}`}
    >
      <RouteMap routeId={item.id} latlons={latlons} />
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-baseline gap-3 mb-3">
          <span className="font-heading text-stroke text-3xl font-black tracking-tighter">0{idx + 1}</span>
          <h3 className="font-heading font-bold text-white text-xl sm:text-2xl tracking-tight">{item.title}</h3>
        </div>
        <p className="text-zinc-400 text-sm leading-relaxed flex-1">{item.desc}</p>
        <div className="grid grid-cols-3 gap-2 mt-6 pt-6 border-t border-white/10 text-center">
          <div>
            <RouteIcon className="w-3.5 h-3.5 text-scooter-yellow mx-auto mb-1" />
            <div className="text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-500">{t.routes.stats.distance}</div>
            <div className="text-white text-sm font-bold font-heading">{item.stats.distance}</div>
          </div>
          <div>
            <Clock className="w-3.5 h-3.5 text-scooter-yellow mx-auto mb-1" />
            <div className="text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-500">{t.routes.stats.duration}</div>
            <div className="text-white text-sm font-bold font-heading">{item.stats.duration}</div>
          </div>
          <div>
            <TrendingUp className="w-3.5 h-3.5 text-scooter-yellow mx-auto mb-1" />
            <div className="text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-500">{t.routes.stats.elev}</div>
            <div className="text-white text-sm font-bold font-heading">{item.stats.elev}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button
            onClick={handleGpx}
            disabled={loading}
            className="h-11 border border-white/15 text-white text-[10px] tracking-[0.25em] uppercase font-bold hover:bg-white/5 hover:border-white/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            data-testid={`route-maps-${item.id}`}
            title="Apri il percorso su Google Maps"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
            {t.routes.open_maps}
          </button>
          <button
            onClick={onBook}
            className="h-11 bg-scooter-yellow text-black text-[10px] tracking-[0.25em] uppercase font-bold hover:bg-scooter-yellow-hover transition-all flex items-center justify-center gap-2 group/btn"
            data-testid={`route-book-${item.id}`}
          >
            <MapPin className="w-3 h-3" />
            {t.routes.cta_short}
            <ArrowRight className="w-3 h-3 group-hover/btn:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Itineraries({ onBook }) {
  const { t } = useI18n();

  useEffect(() => {
    delete L.Icon.Default.prototype._getIconUrl;
  }, []);

  return (
    <section id="routes" className="relative py-24 sm:py-32 bg-[#080808]" data-testid="routes-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-scooter-yellow mb-3">{t.routes.label}</div>
            <h2 className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] whitespace-pre-line">{t.routes.title}</h2>
          </div>
          <div className="lg:col-span-7 flex items-end">
            <p className="text-zinc-400 text-lg leading-relaxed max-w-2xl">{t.routes.intro}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.routes.items.map((it, idx) => (
            <RouteCard key={it.id} item={it} idx={idx} onBook={onBook} />
          ))}
        </div>
      </div>
    </section>
  );
}
